import {
  type LoginRequest,
  type SessionUser,
  type TokenResponse,
  TokenResponseSchema,
} from '@vision360/contracts';
import { ApiError, NetworkError, toApiError } from '../api/errors';
import { apiUrl } from '../api/origin';
import type { TokenStore } from '../platform/types';

export const AUTH_BASE = '/api/v1/auth';
/** Refresh this long before the access token expires. */
const REFRESH_LEAD_MS = 60_000;
const MIN_REFRESH_DELAY_MS = 5_000;
/** Web Locks name serialising refreshes across tabs (TechSpec §13.1.1). */
export const REFRESH_LOCK = 'vision360:auth-refresh';

export interface SessionDeps {
  /** The platform's token store (TechSpec §8.2): memory on web, keychain on desktop. */
  tokens: TokenStore;
  /**
   * `web`: the refresh token is the httpOnly cookie and never leaves it.
   * `desktop`: the server returns it in the body and it lives in the keychain
   * (TechSpec §13.1.2). Defaults to `web`.
   */
  client?: 'web' | 'desktop';
  fetch?: typeof fetch;
  /** navigator.locks where available. Absent in tests and old browsers. */
  locks?: Pick<LockManager, 'request'> | undefined;
  now?: () => number;
  setTimer?: (fn: () => void, ms: number) => unknown;
  clearTimer?: (handle: unknown) => void;
}

export type SessionListener = (user: SessionUser | null) => void;

export interface Session {
  /** Restores the session from the refresh cookie. Null when signed out. */
  bootstrap(): Promise<SessionUser | null>;
  login(credentials: Omit<LoginRequest, 'client'>): Promise<SessionUser>;
  /**
   * Rotates the refresh token. Concurrent callers share one request, and tabs
   * take turns through a Web Lock, so the same token is never presented twice
   * — which the server would treat as theft and answer by revoking the family.
   * Resolves null when the session is over.
   */
  refresh(): Promise<SessionUser | null>;
  logout(): Promise<void>;
  accessToken(): Promise<string | null>;
  /** Called when the session ends on its own, e.g. a failed background refresh. */
  subscribe(listener: SessionListener): () => void;
}

export function createSession(deps: SessionDeps): Session {
  const doFetch = deps.fetch ?? ((...args: Parameters<typeof fetch>) => fetch(...args));
  const now = deps.now ?? Date.now;
  const setTimer = deps.setTimer ?? ((fn: () => void, ms: number) => setTimeout(fn, ms));
  const clearTimer = deps.clearTimer ?? ((h: unknown) => clearTimeout(h as number));
  const listeners = new Set<SessionListener>();
  const client = deps.client ?? 'web';

  let inflight: Promise<SessionUser | null> | null = null;
  let timer: unknown = null;

  function notify(user: SessionUser | null): void {
    for (const listener of listeners) listener(user);
  }

  async function post(path: string, body: unknown, headers: Record<string, string> = {}) {
    try {
      return await doFetch(apiUrl(`${AUTH_BASE}${path}`), {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json', ...headers },
        body: JSON.stringify(body),
      });
    } catch (cause) {
      throw new NetworkError(cause);
    }
  }

  async function adopt(response: TokenResponse): Promise<SessionUser> {
    await deps.tokens.set({
      accessToken: response.accessToken,
      expiresAt: now() + response.expiresIn * 1000,
      ...(client === 'desktop' && response.refreshToken
        ? { refreshToken: response.refreshToken }
        : {}),
    });
    schedule(response.expiresIn * 1000);
    return response.user;
  }

  function schedule(expiresInMs: number): void {
    if (timer !== null) clearTimer(timer);
    const delay = Math.max(expiresInMs - REFRESH_LEAD_MS, MIN_REFRESH_DELAY_MS);
    timer = setTimer(() => {
      timer = null;
      refresh()
        .then((user) => {
          if (user === null) notify(null);
        })
        // Offline: keep the session; the next API call will retry the refresh.
        .catch(() => undefined);
    }, delay);
  }

  async function end(): Promise<void> {
    if (timer !== null) clearTimer(timer);
    timer = null;
    await deps.tokens.clear();
  }

  /** The refresh token itself, for desktop only; web sends the cookie. */
  async function refreshTokenBody(): Promise<{ refreshToken?: string }> {
    if (client !== 'desktop') return {};
    const refreshToken = (await deps.tokens.get())?.refreshToken;
    return refreshToken ? { refreshToken } : {};
  }

  async function rotate(): Promise<SessionUser | null> {
    const body = await refreshTokenBody();
    if (client === 'desktop' && !body.refreshToken) return null;
    const response = await post('/refresh', { client, ...body });
    if (response.status === 401) {
      await end();
      return null;
    }
    if (!response.ok) throw await toApiError(response);
    return adopt(TokenResponseSchema.parse(await response.json()));
  }

  function refresh(): Promise<SessionUser | null> {
    if (inflight) return inflight;
    const run = deps.locks ? deps.locks.request(REFRESH_LOCK, rotate) : rotate();
    inflight = Promise.resolve(run).finally(() => {
      inflight = null;
    });
    return inflight;
  }

  return {
    bootstrap: refresh,
    refresh,

    async login(credentials) {
      const response = await post('/login', { ...credentials, client });
      if (!response.ok) throw await toApiError(response);
      return adopt(TokenResponseSchema.parse(await response.json()));
    },

    async logout() {
      const tokens = await deps.tokens.get();
      try {
        const response = await post(
          '/logout',
          await refreshTokenBody(),
          tokens ? { authorization: `Bearer ${tokens.accessToken}` } : {},
        );
        // An expired access token: rotate once so the server can revoke the family.
        if (response.status === 401 && (await refresh()) !== null) {
          const fresh = await deps.tokens.get();
          await post(
            '/logout',
            await refreshTokenBody(),
            fresh ? { authorization: `Bearer ${fresh.accessToken}` } : {},
          );
        }
      } catch (error) {
        // Signing out locally must not depend on the network.
        if (!(error instanceof NetworkError || error instanceof ApiError)) throw error;
      } finally {
        await end();
      }
    },

    async accessToken() {
      const tokens = await deps.tokens.get();
      if (tokens && tokens.expiresAt - now() > 5_000) return tokens.accessToken;
      const user = await refresh();
      return user ? ((await deps.tokens.get())?.accessToken ?? null) : null;
    },

    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}
